import React from 'react';
import { StopIcon } from './icons';
import { colors, type } from './theme';
import { voiceLabel } from './ttsClient';

// ── now-playing indicator ────────────────────────────────────────────────────
// One fixed bar naming whatever is currently producing sound, so the answer to
// "what am I listening to?" survives scrolling away from the control that
// started it.
//
// This only works because each page owns a single audio slot: exactly one
// source (a showcase card or the reader) can play at a time, and the page
// stops the other when one starts. Before that, every card held its own
// independent <audio> and several could sound at once, so there was no single
// truthful answer to report.
//
// `playing` is either null or { state, label, voice? } where `state` is
// 'loading' | 'playing' | 'paused'.
//
// Pages that render this must also pass `nowPlaying` to their PageShell, which
// widens the bottom padding and lifts the mobile sticky Generate button so
// neither ends up underneath this bar.
const NowPlayingBar = ({ playing, onStop }) => {
  if (!playing) return null;

  const stateLabel =
    playing.state === 'loading'
      ? 'Generating'
      : playing.state === 'paused'
        ? 'Paused'
        : 'Now playing';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 30,
        background: colors.card,
        borderTop: `1px solid ${colors.borderActive}`,
        padding:
          '10px clamp(16px, 5vw, 24px) calc(10px + env(safe-area-inset-bottom))',
        boxShadow: colors.shadow,
      }}
    >
      <div
        style={{
          maxWidth: 960,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: colors.accent,
            flexShrink: 0,
            animation:
              playing.state === 'paused'
                ? 'none'
                : 'shona-nowplaying-pulse 1.4s ease-in-out infinite',
          }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{ ...type.eyebrow, fontSize: 11, color: colors.emphasis }}
          >
            {stateLabel}
          </div>
          {/* A History clip's title is the reader's own words. */}
          <div
            data-clarity-mask="True"
            style={{
              color: colors.text,
              fontSize: 15,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {playing.label}
            {playing.voice && (
              <span style={{ color: colors.textDim, fontWeight: 400 }}>
                {' · '}
                {voiceLabel(playing.voice)}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onStop}
          aria-label="Stop playback"
          title="Stop playback"
          style={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: `1px solid ${colors.borderActive}`,
            background: 'transparent',
            color: colors.accent,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <StopIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};

export default NowPlayingBar;
